import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";

import { useFormValidation } from "../../../hooks/useFormValidation";
import "react-toastify/dist/ReactToastify.css";
import "react-phone-number-input/style.css";
import { toast } from "react-toastify";
import allImages from "../../../assets/images-import";
import { handleErrors } from "../../../utils/errorHandler";
import ComponentHeader from "../../Common/OtherElements/ComponentHeader";
import { usePageLevelAccess } from "../../../hooks/usePageLevelAccess";
import { useNavigate } from "react-router-dom";
import { InputField } from "../../Common/FormElements/InputField";
import { SelectField } from "../../Common/FormElements/SelectField";

import { TextareaField } from "../../Common/FormElements/TextareaField";
import { ImageInputField } from "../../Common/FormElements/ImageInputField";
import { TinyMCEEditorField } from "../../Common/FormElements/TinyMCEEditorField";
import { fetchCategoryDropdowns } from "../../../services/categoryService";
import { fetchSubCategoryDropdowns } from "../../../services/subCategoryService";
import { fetchBrandDropdowns } from "../../../services/brandService";
import { fetchHsnCodeDropdowns } from "../../../services/hsnCodeService";
import {
  fetchTagCollections,
  fetchTagByCollectionByIds,
} from "../../../services/tagCollectionService";

import {
  updateProduct,
  fetchProductById,
  fetchProductDropdowns,
} from "../../../services/productService";

const AddProduct = () => {
  const parseMultiSelectValue = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === "string") {
      return value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }
    return [];
  };
  // Helper function to convert text to URL slug
  const convertToSlug = (text) => {
    return text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "") // Remove special characters except spaces and hyphens
      .replace(/\s+/g, "-") // Replace spaces with hyphens
      .replace(/-+/g, "-") // Replace multiple consecutive hyphens with single hyphen
      .replace(/^-+|-+$/g, ""); // Remove leading and trailing hyphens
  };
  const { id } = useParams();
  // ✅ Move all hooks to the top
  const navigate = useNavigate();
  const PageLevelAccessurl = "product/add";
  const { pageAccessData } = usePageLevelAccess(PageLevelAccessurl);

  // ✅ Form validation hook
  const {
    formErrors,
    isFormValid,
    registerField,
    validateAllFields,
    clearErrors,
    touchAllFields,
    getFieldLabel,
  } = useFormValidation();

  // ✅ Create refs for each field
  const brandIdRef = useRef(null);
  const categoryIdRef = useRef(null);
  const subCategoryIdRef = useRef(null);
  const hsnCodeIdRef = useRef(null);
  const productNameRef = useRef(null);
  const productURLRef = useRef(null);
  const productOrderRef = useRef(null);
  const skuCodeRef = useRef(null);
  const ingredientsRef = useRef(null);
  const originRef = useRef(null);
  const productTagsRef = useRef(null);
  const tagCollectionIdsRef = useRef(null);
  const relatedProductsRef = useRef(null);
  const shortDescriptionRef = useRef(null);
  const fullDescriptionRef = useRef(null);
  const pageTitleRef = useRef(null);
  const metaDescriptionRef = useRef(null);
  const imageInputRef = useRef(null);

  // ✅ Initialize all state properly
  const [formValues, setFormValues] = useState({
    Id: id,
    BrandId: "",
    SubCategoryId: "",
    CategoryId: "",
    HSNCodeId: "",
    ProductName: "",
    ProductURL: "",
    ProductTags: "",
    TagCollectionIds: "",
    RelatedProducts: "",
    SKUCode: "",
    Ingredients: "",
    Origin: "",
    PageTitle: "",
    MetaDescription: "",
    Published: false,
    ProductOrder: 1000,
    FullDescription: "",
    ShortDescription: "",
    ThumbImageFile: null,
  });

  const [imageUrl, setImageUrl] = useState(allImages.ImagePlaceholder);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [hsnCodes, setHsnCodes] = useState([]);
  const [productTags, setProductTags] = useState([]);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [fullDescriptionContent, setFullDiscriptionContent] = useState("");
  const [ingredientsContent, setIngredientsContent] = useState("");
  const [productTagCollections, setProductTagCollections] = useState([]);

  // ✅ Register all fields
  useEffect(() => {
    registerField("BrandId", brandIdRef, "Brand Id");
    registerField("CategoryId", categoryIdRef, "Category Id");
    registerField("SubCategoryId", subCategoryIdRef, "Sub Category Id");
    registerField("HSNCodeId", hsnCodeIdRef, "HSN Code Id");
    registerField("ProductName", productNameRef, "Product Name");
    registerField("ProductURL", productURLRef, "Product URL");
    registerField("ProductOrder", productOrderRef, "Display Order");
    registerField("ProductTags", productTagsRef, "Product Tags");
    registerField("TagCollectionIds", tagCollectionIdsRef, "Tag Collections");
    registerField("RelatedProducts", relatedProductsRef, "Related Products");
    registerField("SKUCode", skuCodeRef, "SKU Code");
    registerField("Ingredients", ingredientsRef, "Ingredients");
    registerField("Origin", originRef, "Origin");
    registerField("ShortDescription", shortDescriptionRef, "Short Description");
    registerField("FullDescription", fullDescriptionRef, "Full Description");
    registerField("PageTitle", pageTitleRef, "Page Title");
    registerField("MetaDescription", metaDescriptionRef, "Meta Description");
    registerField("ThumbImageFile", imageInputRef, "Thumb Image");
  }, [registerField]);

  // Add this useEffect to validate after formValues change
  useEffect(() => {
    // Only validate if form has been touched/submitted before
    if (Object.keys(formErrors).length > 0) {
      const timeoutId = setTimeout(() => {
        validateAllFields();
      }, 100); // Small delay to ensure refs are updated

      return () => clearTimeout(timeoutId);
    }
  }, [formValues, validateAllFields, formErrors]);

  // ✅ Fix page access check with proper dependencies
  useEffect(() => {
    if (pageAccessData) {
      if (!pageAccessData.addAccess || !pageAccessData.viewAccess) {
        navigate("/404-error-page");
      }
    }
  }, [pageAccessData, navigate]);
  // ✅ Fetch brands

  const loadBrands = async () => {
    try {
      const brandData = await fetchBrandDropdowns();
      if (brandData && brandData.result && brandData.result.length > 0) {
        setBrands(brandData.result);
      }
    } catch (error) {
      handleErrors(error);
    }
  };

  const loadCategories = async () => {
    try {
      const categoryData = await fetchCategoryDropdowns();
      if (
        categoryData &&
        categoryData.result &&
        categoryData.result.length > 0
      ) {
        setCategories(categoryData.result);
      }
    } catch (error) {
      handleErrors(error);
    }
  };
  const loadSubCategories = async (categoryId) => {
    try {
      const subCategoryData = await fetchSubCategoryDropdowns();
      if (
        subCategoryData &&
        subCategoryData.result &&
        subCategoryData.result.length > 0
      ) {
        if (categoryId) {
          const filteredSubCategories = subCategoryData.result.filter(
            (subCat) => subCat.categoryId === categoryId
          );
          setSubCategories(filteredSubCategories);
        }
      }
    } catch (error) {
      handleErrors(error);
    }
  };
  const loadHsnCodes = async () => {
    try {
      const hsnCodeData = await fetchHsnCodeDropdowns();
      if (hsnCodeData && hsnCodeData.result && hsnCodeData.result.length > 0) {
        setHsnCodes(hsnCodeData.result);
      }
    } catch (error) {
      handleErrors(error);
    }
  };
  const loadTagCollections = async () => {
    try {
      const productTagData = await fetchTagCollections();
      if (
        productTagData &&
        productTagData.result &&
        productTagData.result.length > 0
      ) {
        setProductTagCollections(productTagData.result);
      }
    } catch (error) {
      handleErrors(error);
    }
  };
  const loadProductTags = async (collectionIds) => {
    try {
      if (collectionIds) {
        const productTagData = await fetchTagByCollectionByIds(collectionIds);
        if (
          productTagData &&
          productTagData.result &&
          productTagData.result.length > 0
        ) {
          setProductTags(productTagData.result);
        }
      }
    } catch (error) {
      handleErrors(error);
    }
  };

  const loadRelatedProducts = async () => {
    try {
      const productsData = await fetchProductDropdowns();
      if (
        productsData &&
        productsData.result &&
        productsData.result.length > 0
      ) {
        setRelatedProducts(productsData.result);
      }
    } catch (error) {
      handleErrors(error);
    }
  };
  useEffect(() => {
    loadBrands();
    loadCategories();
    loadSubCategories();
    loadHsnCodes();
    loadTagCollections();
    loadProductTags();
    loadRelatedProducts();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetchProductById(id);
        if (response && response.isSuccess && response.result) {
          loadSubCategories(response.result.categoryId);
          loadProductTags(
            parseMultiSelectValue(response.result.tagCollectionIds)
          );
          setFormValues({
            Id: response.result.id,
            BrandId: response.result.brandId || "",
            SubCategoryId: response.result.subCategoryId || "",
            CategoryId: response.result.categoryId || "",
            HSNCodeId: response.result.hsnCodeId || "",
            ProductName: response.result.productName || "",
            ProductURL: response.result.productURL || "",
            ProductTags: parseMultiSelectValue(response.result.productTags),
            RelatedProducts: parseMultiSelectValue(
              response.result.relatedProducts
            ),
            TagCollectionIds: parseMultiSelectValue(
              response.result.tagCollectionIds
            ),
            SKUCode: response.result.skuCode || "",
            Ingredients: response.result.ingredients || "",
            Origin: response.result.origin || "",
            PageTitle: response.result.pageTitle || "",
            MetaDescription: response.result.metaDescription || "",
            Published: response.result.published ?? false,
            ProductOrder: response.result.productOrder ?? 1000,
            FullDescription: response.result.fullDescription || "",
            ShortDescription: response.result.shortDescription || "",
            ThumbImageFile: null, // reset file input
          });
          setFullDiscriptionContent(response.result.fullDescription);
          setIngredientsContent(response.result.ingredients);
          // Set image URL if available
          if (response.result.thumbImage) {
            setImageUrl(response.result.thumbImage);
          } else {
            setImageUrl(allImages.ImagePlaceholder);
          }
        }
      } catch (error) {
        handleErrors(error);
      }
    };
    fetchData();
  }, [id]);

  // ✅ Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      if (imageUrl && imageUrl.startsWith("blob:")) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [imageUrl]);

  const handleFullDiscriptionEditorChange = (content) => {
    setFullDiscriptionContent(content);
    setFormValues((prevData) => ({
      ...prevData,
      FullDescription: content,
    }));
  };

  const handleIngredientsEditorChange = (content) => {
    setIngredientsContent(content);
    setFormValues((prevData) => ({
      ...prevData,
      Ingredients: content,
    }));
  };

  const handleInputChange = (e) => {
    const { id, type, value, checked } = e.target;
    const inputValue = type === "checkbox" ? checked : value;

    setFormValues((prev) => {
      const newValues = {
        ...prev,
        [id]: inputValue,
      };

      // Auto-populate ProductURL when ProductName changes
      // Only auto-populate if URL field is empty or was previously auto-generated
      if (id === "ProductName") {
        // Check if URL field is empty or matches the slug of previous category name
        const previousSlug = convertToSlug(prev.ProductName);
        const shouldAutoPopulate =
          !prev.ProductURL || prev.ProductURL === previousSlug;

        if (shouldAutoPopulate) {
          newValues.ProductURL = convertToSlug(inputValue);
        }
      }

      if (id === "CategoryId") {
        loadSubCategories(inputValue);
      }
      if (id === "TagCollectionIds") {
        loadProductTags(inputValue);
      }
      return newValues;
    });
  };

  // ✅ Fix file handling with cleanup
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Clean up previous URL
      if (imageUrl && imageUrl.startsWith("blob:")) {
        URL.revokeObjectURL(imageUrl);
      }

      const newImageUrl = URL.createObjectURL(file);
      setImageUrl(newImageUrl);
      setFormValues((prevState) => ({ ...prevState, ThumbImageFile: file }));
    }
  };

  // ✅ Reset all form fields
  const resetAllFields = () => {
    const fieldRefs = {
      brandIdRef,
      subCategoryIdRef,
      categoryIdRef,
      hsnCodeIdRef,
      productNameRef,
      productOrderRef,
      productURLRef,
      productTagsRef,
      tagCollectionIdsRef,
      relatedProductsRef,
      skuCodeRef,
      ingredientsRef,
      originRef,
      shortDescriptionRef,
      fullDescriptionRef,
      pageTitleRef,
      metaDescriptionRef,
      imageInputRef,
    };

    Object.values(fieldRefs).forEach((ref) => {
      if (ref.current && ref.current.reset) {
        ref.current.reset();
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // ✅ Touch all fields to show validation errors
    touchAllFields();

    // ✅ Validate all fields using refs
    const isValid = validateAllFields();

    if (!isValid) {
      toast.error("Please fix all validation errors before submitting.");
      return;
    }

    try {
      setIsButtonDisabled(true);

      console.log(JSON.stringify(formValues));
      var responseData = await updateProduct(formValues);

      // Clean up current image URL
      if (imageUrl && imageUrl.startsWith("blob:")) {
        URL.revokeObjectURL(imageUrl);
      }

      // ✅ Clear form errors
      clearErrors();

      if (responseData?.isSuccess) {
        toast.success(responseData?.statusMessage);
      } else {
        toast.error(responseData?.statusMessage);
      }
    } catch (error) {
      handleErrors(error);
    } finally {
      setIsButtonDisabled(false);
      loadRelatedProducts();
    }
  };

  return (
    <>
      <ComponentHeader title="Add Product" />
      {/* ✅ Optional: Show form-level validation status */}
      {Object.keys(formErrors).length > 0 && (
        <div className="alert alert-warning mb-3">
          <strong>Please fix the following errors:</strong>
          <ul className="mb-0 mt-2">
            {Object.entries(formErrors).map(([fieldId, error]) => (
              <li key={fieldId}>
                <strong>{getFieldLabel(fieldId)}:</strong> {error}
              </li>
            ))}
          </ul>
        </div>
      )}

      <form onSubmit={handleSubmit} method="POST">
        <div className="row">
          <div className="col-xxl-8">
            <div className="card mt-xxl-n5">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-sm-1 mt-sm-1">Add Product</h5>
                <div className="d-flex align-items-center">
                  <label htmlFor="Published" className="form-label pe-3">
                    Published Product
                  </label>
                  <div className="form-check form-switch form-switch-custom form-switch-primary">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="Published"
                      name="Published"
                      checked={formValues.Published}
                      onChange={handleInputChange}
                    />
                    <label className="form-check-label" htmlFor="Published">
                      {formValues.Published ? "Yes" : "No"}
                    </label>
                  </div>
                </div>
              </div>

              <div className="card-body p-4">
                <div className="row">
                  <div className="col-lg-12 col-md-6 col-sm-12">
                    <InputField
                      ref={productNameRef}
                      label="Product Name"
                      id="ProductName"
                      value={formValues.ProductName}
                      placeholder="Enter your Product Name"
                      onChange={handleInputChange}
                      required={true}
                      validationType="alphanumeric"
                      maxLength={300}
                      showError={true}
                    />
                  </div>

                  <div className="col-lg-12 col-md-12 col-sm-12">
                    <InputField
                      ref={productURLRef}
                      label="Product URL"
                      id="ProductURL"
                      value={formValues.ProductURL}
                      placeholder="Auto generated URL"
                      onChange={handleInputChange}
                      maxLength={300}
                      required={true}
                      validationType="url"
                    />
                  </div>
                  <div className="col-lg-4 col-md-6 col-sm-12">
                    <InputField
                      ref={productOrderRef}
                      label="Display Order"
                      id="ProductOrder"
                      value={formValues.ProductOrder}
                      onChange={handleInputChange}
                      placeholder="Enter Display Order"
                      validationType="numberonly"
                      maxLength={5}
                      required={true}
                    />
                  </div>
                  <div className="col-lg-4 col-md-6 col-sm-12">
                    <InputField
                      ref={skuCodeRef}
                      label="SKU Code"
                      id="SKUCode"
                      value={formValues.SKUCode}
                      onChange={handleInputChange}
                      placeholder="Enter SKU Code"
                      validationType="alphanumeric"
                      maxLength={10}
                      required={true}
                    />
                  </div>
                  <div className="col-lg-4 col-md-6 col-sm-12">
                    <InputField
                      ref={originRef}
                      label="Country of Origin"
                      id="Origin"
                      value={formValues.Origin}
                      onChange={handleInputChange}
                      placeholder="Enter Country of Origin"
                      validationType="alphaonly"
                      maxLength={100}
                      required={true}
                    />
                  </div>

                  <div className="col-lg-12 col-md-12 col-sm-12">
                    <TextareaField
                      ref={shortDescriptionRef}
                      label="Short Description"
                      id="ShortDescription"
                      value={formValues.ShortDescription}
                      onChange={handleInputChange}
                      placeholder="Enter Short Description"
                      required={true}
                      showCharCount={true}
                      maxLength={1500}
                    />
                  </div>
                  <div className="col-lg-12 col-md-12 col-sm-12">
                    <TinyMCEEditorField
                      ref={ingredientsRef}
                      id="Ingredients"
                      label="Ingredients"
                      content={ingredientsContent}
                      handleEditorChange={handleIngredientsEditorChange}
                      required={true}
                      height={400}
                    />
                  </div>
                  <div className="col-lg-12 col-md-12 col-sm-12">
                    <TinyMCEEditorField
                      ref={fullDescriptionRef}
                      id="FullDescription"
                      label="Full Description"
                      content={fullDescriptionContent}
                      handleEditorChange={handleFullDiscriptionEditorChange}
                      required={true}
                    />
                  </div>
                </div>

                <div className="text-start">
                  <button
                    type="submit"
                    className="btn btn-secondary"
                    disabled={isButtonDisabled || !isFormValid}
                  >
                    {isButtonDisabled ? "Updating..." : "update"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xxl-4">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-sm-1 mt-sm-1">
                  Catalogue <span className="required-field">*</span>
                </h5>
              </div>
              <div className="card-body p-4">
                <SelectField
                  ref={brandIdRef}
                  label="Brand"
                  id="BrandId"
                  value={formValues.BrandId}
                  onChange={handleInputChange}
                  options={brands}
                  optionValueKey="id"
                  optionLabelKey="brandName"
                  placeholder="Select Brand"
                  required={true}
                />
                <SelectField
                  ref={categoryIdRef}
                  label="Category"
                  id="CategoryId"
                  value={formValues.CategoryId}
                  onChange={handleInputChange}
                  options={categories}
                  optionValueKey="id"
                  optionLabelKey="categoryName"
                  placeholder="Select Category"
                  required={true}
                />
                <SelectField
                  ref={subCategoryIdRef}
                  label="Sub Category"
                  id="SubCategoryId"
                  value={formValues.SubCategoryId}
                  onChange={handleInputChange}
                  options={subCategories}
                  optionValueKey="id"
                  optionLabelKey="subCategoryName"
                  placeholder="Select Sub Category"
                  required={true}
                />
              </div>
            </div>

            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-sm-1 mt-sm-1">HSN Code</h5>
              </div>
              <div className="card-body p-4">
                <SelectField
                  ref={hsnCodeIdRef}
                  id="HSNCodeId"
                  value={formValues.HSNCodeId}
                  onChange={handleInputChange}
                  options={hsnCodes}
                  optionValueKey="id"
                  optionLabelKey="hsn"
                  placeholder="Select HSN Code"
                  required={true}
                />
              </div>
            </div>

            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-sm-1 mt-sm-1">
                  Product Tags / Related Products
                </h5>
              </div>
              <div className="card-body p-4">
                <SelectField
                  ref={tagCollectionIdsRef}
                  label="Product Tags Collection"
                  id="TagCollectionIds"
                  value={formValues.TagCollectionIds}
                  onChange={handleInputChange}
                  options={productTagCollections}
                  optionValueKey="id"
                  optionLabelKey="collectionName"
                  placeholder="Select Tag Collections"
                  required={false}
                  isMulti={true}
                />
                <SelectField
                  ref={productTagsRef}
                  label="Product Tags"
                  id="ProductTags"
                  value={formValues.ProductTags}
                  onChange={handleInputChange}
                  options={productTags}
                  optionValueKey="id"
                  optionLabelKey="tagName"
                  placeholder="Select Product Tags"
                  required={false}
                  isMulti={true}
                />
                <SelectField
                  label="Related Products"
                  ref={relatedProductsRef}
                  id="RelatedProducts"
                  value={formValues.RelatedProducts}
                  onChange={handleInputChange}
                  options={relatedProducts}
                  optionValueKey="id"
                  optionLabelKey="productName"
                  placeholder="Select Related Products"
                  required={false}
                  isMulti={true}
                />
              </div>
            </div>

            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-sm-1 mt-sm-1">SEO Manager</h5>
              </div>
              <div className="card-body p-4">
                <TextareaField
                  ref={pageTitleRef}
                  label="Page Title"
                  id="PageTitle"
                  value={formValues.PageTitle}
                  onChange={handleInputChange}
                  placeholder="Enter Page Title"
                  showCharCount={true}
                  maxLength={300}
                  suggestedLength={70}
                />
                <TextareaField
                  ref={metaDescriptionRef}
                  label="Meta Description"
                  id="MetaDescription"
                  value={formValues.MetaDescription}
                  onChange={handleInputChange}
                  placeholder="Enter Meta Description"
                  showCharCount={true}
                  maxLength={500}
                  suggestedLength={160}
                />
              </div>
            </div>
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-sm-1 mt-sm-1">
                  Thumb Image <span className="required-field">*</span>
                </h5>
              </div>
              <div className="card-body p-4">
                <ImageInputField
                  ref={imageInputRef}
                  id="ThumbImageFile"
                  imageUrl={imageUrl}
                  onFileChange={handleFileChange}
                  required={false}
                />
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default AddProduct;
